import { MediaService } from "../../services/media.service.js";
import { getFirestore, doc } from "firebase/firestore";
import { app } from "../../firebase-config.js";

const db = getFirestore(app);

/**
 * [Webtoon Hub]
 * Manages Series Metadata & Episode List.
 * Refactored from WebtoonEditor.js to reduce bloat.
 */
export class WebtoonHub {
    constructor(container, controller) {
        this.container = container;
        this.controller = controller; // Reference to main Editor class for callbacks
        this.data = controller.data;
        this.seriesId = controller.seriesId;
        this.episodes = controller.episodes;
    }

    render() {
        this.container.innerHTML = `
        <div class="w-full h-full flex flex-col bg-zinc-900 text-white font-sans relative overflow-hidden" style="background-image: url('/login.webp'); background-size: cover; background-position: center;">
            <div class="absolute inset-0 bg-zinc-900/90 backdrop-blur-md z-0"></div>

            <div class="relative z-10 w-full h-full flex flex-col max-w-[1920px] mx-auto shadow-2xl">
                <!-- Header -->
                <header class="h-16 border-b border-white/10 flex items-center justify-between px-6 bg-black/20 backdrop-blur-md flex-shrink-0">
                    <div class="flex items-center gap-4">
                        <button id="btn-back-main" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-white/70 hover:text-white">
                            <span class="material-symbols-outlined">arrow_back</span>
                        </button>
                        <div class="flex items-center gap-2">
                             <span class="material-symbols-outlined text-indigo-400">collections_bookmark</span>
                             <h1 class="font-bold text-lg text-white tracking-wide">Webtoon Studio</h1>
                        </div>
                    </div>
                </header>

                <div class="flex-grow flex overflow-hidden">
                    <!-- Left: Meta Editor -->
                    <aside class="w-[400px] border-r border-white/10 bg-black/20 flex flex-col flex-shrink-0 backdrop-blur-sm">
                        <div class="flex-grow overflow-y-auto p-6 space-y-6 custom-scrollbar">
                             ${this.renderMetaForm()}
                        </div>
                        <div class="p-4 border-t border-white/10 bg-black/20 backdrop-blur-sm">
                             <button id="btn-save-meta" class="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl shadow-lg shadow-indigo-900/20 flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
                                <span class="material-symbols-outlined text-sm">save</span> Save Metadata
                            </button>
                        </div>
                    </aside>

                    <!-- Right: Episode List -->
                    <main class="flex-grow bg-black/50 p-6 md:p-10 overflow-y-auto custom-scrollbar">
                        <div class="max-w-6xl mx-auto space-y-6">
                            <div class="flex justify-between items-end mb-2">
                                <div>
                                    <h2 class="text-2xl font-bold text-white mb-1">Episodes</h2>
                                    <p class="text-sm text-zinc-400">Manage your series episodes here.</p>
                                </div>
                                <button id="btn-add-ep" class="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-purple-900/30 text-sm font-bold flex items-center gap-2 transition-all transform hover:-translate-y-0.5">
                                    <span class="material-symbols-outlined text-lg">add_circle</span> New Episode
                                </button>
                            </div>

                            <div id="ep-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <!-- Cards injected here -->
                            </div>
                             
                             ${this.episodes.length === 0 ? this.renderEmptyState() : ''}
                        </div>
                    </main>
                </div>
            </div>
        </div>
        `;

        this.bindEvents();
        this.renderEpisodeList();
    }

    renderMetaForm() {
        return `
            <div class="space-y-4">
                <label class="block text-xs font-bold text-indigo-300 uppercase tracking-wider">Series Info</label>
                
                <div class="space-y-1">
                    <span class="text-xs text-zinc-400 ml-1">Title</span>
                    <input class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-sm text-white focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all" id="hub-title" value="${this.data.title}" placeholder="Series Title">
                </div>

                <div class="space-y-1">
                    <span class="text-xs text-zinc-400 ml-1">Cover Image (Full URL)</span>
                    <div class="flex items-center gap-2">
                        <input class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-xs text-zinc-300 font-mono focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all" id="hub-cover" value="${this.data.coverImage || ''}" placeholder="Image URL (Managed in Series Settings)" readonly>
                    </div>
                    <!-- Cover Preview -->
                    <div class="w-full aspect-[2/3] bg-black/40 rounded-lg overflow-hidden border border-white/5 mt-2 shadow-lg">
                        <img src="${this.data.coverImage || ''}" class="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onerror="this.style.display='none'" onload="this.style.display='block'">
                    </div>
                </div>

                <div class="space-y-1">
                    <span class="text-xs text-zinc-400 ml-1">Tags</span>
                    <input class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-sm text-white focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all" id="hub-tags" value="${(this.data.tags || []).join(', ')}" placeholder="Action, Fantasy, Drama">
                </div>

                <div class="space-y-1">
                    <span class="text-xs text-zinc-400 ml-1">Synopsis</span>
                    <textarea class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-sm text-zinc-300 h-32 resize-none focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all" id="hub-desc" placeholder="Story description...">${this.data.description || ''}</textarea>
                </div>

                ${this.renderMetaFields()}
            </div>
        `;
    }

    isAdmin() {
        const user = window.user;
        return user && (user.isSuperAdmin || ['admin', 'super_admin'].includes(user.role));
    }

    renderMetaFields() {
        if (!this.isAdmin()) return '';
        return `
            <div class="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/5 bg-yellow-500/5 p-3 rounded-xl border border-yellow-500/10">
                <div class="col-span-2 text-[10px] font-bold text-yellow-500 uppercase flex items-center gap-1 mb-2">
                    <span class="material-symbols-outlined text-sm">lock_person</span> Admin Control
                </div>
                <div class="space-y-1">
                    <label class="block text-xs font-bold text-zinc-500 uppercase">ISBN</label>
                    <input class="w-full bg-zinc-900/80 border border-zinc-700/50 rounded-lg p-2.5 text-sm text-yellow-500 font-mono focus:outline-none focus:border-yellow-500 transition-all" id="hub-isbn" value="${this.data.isbn || ''}" placeholder="-">
                </div>
                <div class="space-y-1">
                    <label class="block text-xs font-bold text-zinc-500 uppercase">UCI</label>
                    <input class="w-full bg-zinc-900/80 border border-zinc-700/50 rounded-lg p-2.5 text-sm text-yellow-500 font-mono focus:outline-none focus:border-yellow-500 transition-all" id="hub-uci" value="${this.data.uci || ''}" placeholder="-">
                </div>
            </div>
        `;
    }

    renderEmptyState() {
        return `
            <div class="flex flex-col items-center justify-center py-20 text-zinc-600 opacity-50 border-2 border-dashed border-zinc-800 rounded-2xl">
                <span class="material-symbols-outlined text-6xl mb-2">post_add</span>
                <p class="text-lg font-medium">No episodes yet.</p>
                <p class="text-sm">Click the button above to create one.</p>
            </div>
        `;
    }

    renderEpisodeList() {
        const grid = this.container.querySelector('#ep-grid');
        grid.innerHTML = '';

        this.episodes.forEach((ep, idx) => {
            // [REFACTOR] Adapter now guarantees status is 'published' if undefined.
            const isDraft = ep.status === 'draft';
            const card = document.createElement('div');
            card.className = "bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-purple-500/50 transition-all group shadow-lg flex flex-col";

            // 16:9 Aspect Ratio for Webtoon
            card.innerHTML = `
                <div class="aspect-video w-full bg-zinc-950 relative overflow-hidden group-hover:opacity-100 opacity-80 transition-opacity border-b border-white/5">
                    ${ep.thumb
                    ? `<img src="${ep.thumb}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700">`
                    : `<div class="w-full h-full flex items-center justify-center text-zinc-700 flex-col gap-2">
                             <span class="material-symbols-outlined text-4xl opacity-50">image</span>
                             <span class="text-[10px] font-mono opacity-50">No Thumbnail</span>
                           </div>`
                }
                    
                    <!-- Status Badge -->
                    <div class="absolute top-2 right-2 px-2 py-1 rounded text-[10px] font-bold border backdrop-blur-md shadow-lg flex items-center gap-1
                        ${isDraft
                    ? 'bg-zinc-900/90 text-zinc-400 border-zinc-700'
                    : 'bg-indigo-600/90 text-white border-indigo-400/50'
                }">
                        <span class="material-symbols-outlined text-[12px]">
                            ${isDraft ? 'lock' : 'rocket_launch'}
                        </span>
                        ${isDraft ? 'DRAFT' : 'PUBLISHED'}
                    </div>

                    <!-- Episode Number -->
                    <div class="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-xs font-bold text-white border border-white/10 backdrop-blur-md">
                        #${idx + 1}
                    </div>
                </div>

                <div class="p-4 flex items-center justify-between gap-4">
                    <div class="min-w-0">
                        <h3 class="font-bold text-white truncate text-sm group-hover:text-purple-400 transition-colors">${ep.title || 'Untitled Episode'}</h3>
                        <p class="text-[10px] text-zinc-500 font-mono mt-0.5">${(ep.links || []).length} pages • ${this.formatDate(ep.updatedAt)}</p>
                    </div>
                    <div class="flex items-center gap-2">
                         <button class="btn-delete-ep w-9 h-9 rounded-lg bg-zinc-800 hover:bg-red-500/10 text-zinc-500 hover:text-red-500 transition-colors flex items-center justify-center border border-white/5 hover:border-red-500/20 shadow-md">
                            <span class="material-symbols-outlined text-sm">delete</span>
                        </button>
                        <button class="btn-edit-ep w-9 h-9 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white transition-colors flex items-center justify-center border border-white/5 shadow-md">
                            <span class="material-symbols-outlined text-sm">edit</span>
                        </button>
                    </div>
                </div>
            `;

            card.querySelector('.btn-edit-ep').onclick = () => {
                // [FIX] Inject Legacy Data into Controller State
                this.controller.files = (ep.links || []).map((url, i) => ({
                    id: `legacy_${Date.now()}_${i}`,
                    url: url,
                    isLocal: false,
                    name: `Page ${i + 1}`,
                    status: 'done'
                }));

                this.controller.currentEditorIndex = idx;
                this.controller.render();
            };

            card.querySelector('.btn-delete-ep').onclick = (e) => {
                e.stopPropagation();
                this.deleteEpisode(idx);
            };

            grid.appendChild(card);
        });
    }

    formatDate(timestamp) {
        if (!timestamp) return 'Just now';
        // Handle Firestore Timestamp or Date string
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    }

    bindEvents() {
        this.container.querySelector('#btn-back-main').onclick = () => window.router.navigate('/studio');
        this.container.querySelector('#btn-save-meta').onclick = () => this.controller.saveMeta();

        this.container.querySelector('#btn-add-ep').onclick = () => {
            // [UUID Migration] Use crypto.randomUUID() for robust ID
            const newId = crypto.randomUUID();
            this.episodes.push({
                id: newId, // Pre-assign ID
                title: `Episode ${this.episodes.length + 1}`,
                links: [],
                thumb: "",
                status: 'draft',
                order: this.episodes.length, // [Order Logic] Append to end
                updatedAt: new Date()
            });
            this.controller.currentEditorIndex = this.episodes.length - 1;
            this.controller.triggerNewEpisode(); // Helper to clear temp files
            this.controller.render();
        };

        // Cover Upload - REMOVED (Managed in Series Settings)
    }

    async deleteEpisode(index) {
        const ep = this.episodes[index];

        // [UI Protocol] Use Custom Modal
        const confirmed = await window.Modal.confirm(
            `정말 삭제하시겠습니까?\n제목: ${ep.title || 'Untitled'}\n\n이 작업은 되돌릴 수 없습니다.`,
            "에피소드 삭제"
        );
        if (!confirmed) return;

        // Visual Feedback
        const grid = this.container.querySelector('#ep-grid');
        const card = grid.children[index];
        if (card) card.style.opacity = '0.5';

        try {
            // Delegate to Controller (Handles both Subcollection and Legacy Sync)
            await this.controller.deleteEpisode(index);

            // Re-render
            this.renderEpisodeList();

        } catch (e) {
            console.error("Delete failed", e);
            window.Modal.alert("삭제에 실패했습니다: " + e.message, "오류");
            if (card) card.style.opacity = '1';
        }
    }
}
